import React, { useState, useEffect } from 'react';
import api from '../services/api';

export default function PermissionsPage() {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ key: '', label: '', description: '' });
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/permissions')
      .then(res => setPermissions(res.data))
      .catch(() => setError('Erro ao carregar permissões'))
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await api.post('/permissions', form);
      setPermissions(p => [...p, res.data]);
      setForm({ key: '', label: '', description: '' });
      setShowForm(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao criar permissão');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm(`Excluir a permissão "${key}"? Todos os usuários perderão esse acesso.`)) return;
    try {
      await api.delete(`/permissions/${key}`);
      setPermissions(p => p.filter(x => x.key !== key));
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao excluir permissão');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400 text-sm">Carregando...</div>;
  if (error) return <div className="text-red-500 text-sm p-4">{error}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Catálogo de Permissões</h2>
          <p className="text-sm text-gray-500 mt-0.5">Gerencie quais funcionalidades existem no sistema</p>
        </div>
        <button onClick={() => setShowForm(s => !s)}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2.5 rounded-xl transition flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nova permissão
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-2xl border border-gray-100 p-6 mb-6 space-y-4">
          <h3 className="font-medium text-gray-900 text-sm">Nova permissão</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Chave (ex: view_contracts)</label>
              <input value={form.key} onChange={e => setForm(f => ({ ...f, key: e.target.value }))} required
                pattern="[a-z0-9_]+"
                title="Apenas letras minúsculas, números e underscore"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="view_contracts" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Rótulo</label>
              <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} required
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ver Contratos" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Descrição (opcional)</label>
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Acesso à lista de contratos" />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => setShowForm(false)}
              className="border border-gray-200 text-gray-700 px-4 py-2 rounded-xl text-sm hover:bg-gray-50 transition">
              Cancelar
            </button>
            <button type="submit" disabled={creating}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-4 py-2 rounded-xl text-sm transition">
              {creating ? 'Criando...' : 'Criar permissão'}
            </button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3.5">Chave</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3.5">Rótulo</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3.5">Descrição</th>
              <th className="px-6 py-3.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {permissions.map(p => (
              <tr key={p.key} className="hover:bg-gray-50/50 transition">
                <td className="px-6 py-4">
                  <code className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-lg font-mono">{p.key}</code>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{p.label}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{p.description || '—'}</td>
                <td className="px-6 py-4 text-right">
                  <button onClick={() => handleDelete(p.key)}
                    className="text-xs text-red-500 hover:text-red-700 font-medium px-3 py-1.5 rounded-lg hover:bg-red-50 transition">
                    Excluir
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
